import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Input } from '@/components/_untitled/base/input/input';
import { SocialButton } from '@/components/_untitled/base/buttons/social-button';
import { Tabs } from '@/components/_untitled/application/tabs/tabs';
import { AlertCircle, Mail01 } from '@untitledui/icons';
import { useAuth } from '../../hooks/useAuth';
import { authApi } from '@/api/authApi';
import type { KycStepProps } from '../../config/kycSteps.config';
import { Checkbox } from "@/components/_untitled/base/checkbox/checkbox.tsx";
import { build } from '@/config/routes';
import { StepFooter } from '../StepFooter';
import { i18n, prefixes, useTypedTranslation } from '@/locales';
import { TERMS_VERSION } from '@/constants/versions';

type AuthMode = 'login' | 'signup';

const Line = () => <div className="flex-1 w-full border-t border-gray-200"/>

export function AuthMethodStep({ data, onComplete, onNavigateToStep }: KycStepProps) {
    const [tAuthMethod] = useTypedTranslation(prefixes('kyc.steps.authMethod'));
    const { login, setUser } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    // Read initial mode from URL query param, default to 'login'
    const initialMode = (searchParams.get('kycModalTab') === 'signup' ? 'signup' : 'login') as AuthMode;
    const [mode, setMode] = useState<AuthMode>(initialMode);
    const [email, setEmail] = useState(data.email || '');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<{ title: string; message: string } | null>(null);
    const [termsAgreement, setTermsAgreement] = useState(false);

    // Clean up kycModalTab param after reading it
    useEffect(() => {
        if (searchParams.has('kycModalTab')) {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('kycModalTab');
            setSearchParams(newParams, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const canProceed = mode === 'login' ? !!email && !!password : !!email && termsAgreement;

    const handleGoogleLogin = useCallback(() => {
        // Save current location for return navigation
        sessionStorage.setItem('kycReturnTo', window.location.pathname);

        // This will be read by CallbackPage and sent via updatePreferences
        if (mode === 'signup') {
            localStorage.setItem('acceptedTermsVersion', TERMS_VERSION);
        }

        // Google OAuth will redirect the page
        login();
    }, [login, mode]);

    const handleEmailAuth = async () => {
        try {
            setLoading(true);
            setError(null);

            if (mode === 'signup') {
                // Check if email exists
                const emailCheck = await authApi.checkEmail(email);

                if (emailCheck.exists) {
                    // Email already registered - show appropriate message based on primary method
                    if (emailCheck.primaryMethod === 'google') {
                        setError({
                            title: tAuthMethod('useGoogleTitle'),
                            message: tAuthMethod('useGoogleMessage')
                        });
                    } else {
                        // Has password or unknown method - switch to login mode
                        setMode('login');
                        setError({
                            title: tAuthMethod('emailExistsTitle'),
                            message: tAuthMethod('emailExistsMessage')
                        });
                    }
                    return;
                }

                // Email doesn't exist - proceed to account creation
                onComplete({ email });
            } else {
                const response = await authApi.login(email, password);
                setUser(response.user);

                // Save language preference if not already set (for existing users logging in for first time after this feature)
                if (!response.user.language) {
                    const language = (i18n.language === 'he' || i18n.language === 'en') ? i18n.language : 'he';
                    authApi.updatePreferences({ language }).catch((err) => {
                        console.error('Failed to save language preference after login:', err);
                    });
                }

                onComplete();
            }
        } catch (err: unknown) {
            if (err && typeof err === 'object' && 'response' in err) {
                const axiosError = err as { response?: { data?: { message?: string | string[] } } };
                const backendMessage = axiosError.response?.data?.message;
                const errorMessage = Array.isArray(backendMessage) ? backendMessage.join(', ') : backendMessage;

                const title = mode === 'login'
                    ? tAuthMethod('loginFailedTitle')
                    : tAuthMethod('signupFailedTitle');

                // Translate common backend error messages
                let message = tAuthMethod('genericError');
                if (typeof errorMessage === 'string') {
                    if (errorMessage === 'Invalid credentials' || errorMessage === 'Unauthorized') {
                        message = tAuthMethod('invalidCredentials');
                    } else {
                        message = errorMessage;
                    }
                }
                setError({ title, message });
            } else {
                const title = mode === 'login'
                    ? tAuthMethod('loginFailedTitle')
                    : tAuthMethod('signupFailedTitle');
                const message = err instanceof Error
                    ? err.message
                    : tAuthMethod('genericError');
                setError({ title, message });
            }
        } finally {
            setLoading(false);
        }
    };


    return (
        <div className="flex flex-col gap-6">
            <Tabs selectedKey={ mode } onSelectionChange={ (key) => setMode(key as AuthMode) } className="items-center">
                <Tabs.List
                    type="button-minimal"
                    size="md"
                    items={ [
                        { id: 'signup', label: <span className="px-6">{ tAuthMethod('signUp') }</span> },
                        { id: 'login', label: <span className="px-6">{ tAuthMethod('logIn') }</span> },
                    ] }
                />
            </Tabs>

            <Line/>

            <SocialButton
                social="google"
                theme="gray"
                size="lg"
                onClick={ handleGoogleLogin }
                disabled={ mode === 'signup' && !termsAgreement }
            >
                { tAuthMethod('continueWithGoogle') }
            </SocialButton>

            <div className="relative flex items-center gap-2 text-gray-400">
                <Line/>
                <p className="text-sm font-medium uppercase">{ tAuthMethod('orContinueWithEmail') }</p>
                <Line/>
            </div>

            <div className="flex flex-col gap-6">
                <Input
                    type="email"
                    label={ tAuthMethod('emailAddress') }
                    placeholder="name@example.com"
                    value={ email }
                    onChange={ setEmail }
                    icon={ Mail01 }
                    aria-label={ tAuthMethod('emailAddress') }
                    isRequired
                    data-testid="auth-method-email"
                />

                { mode === 'login' && (
                    <>
                        <Input
                            type="password"
                            label={ tAuthMethod('password') }
                            value={ password }
                            onChange={ setPassword }
                            aria-label={ tAuthMethod('password') }
                            isRequired
                            data-testid="auth-method-password"
                        />
                        <button
                            type="button"
                            onClick={ (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onNavigateToStep?.('forgot-password');
                            } }
                            className="text-sm text-brand-secondary hover:underline self-end -mt-4"
                        >
                            { tAuthMethod('forgotPassword') }
                        </button>
                    </>
                ) }
            </div>

            { error && (
                <div className="flex gap-2 rounded-lg bg-error-50 px-3 py-2 text-error-600">
                    <AlertCircle className="h-5 w-5 mt-0.5"/>
                    <div className="ps-0.5 text-sm">
                        <p className="font-semibold text-error-700">{ error.title }</p>
                        <p>{ error.message }</p>
                    </div>
                </div>
            ) }

            { mode === 'signup' && (
                <Checkbox
                    label={
                        <>
                            { tAuthMethod('termsAgreement') }{ ' ' }
                            <a
                                href={ build.pages('terms-and-conditions') }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-brand-secondary hover:underline"
                                onClick={ (e) => e.stopPropagation() }
                            >
                                { tAuthMethod('termsLink') }
                            </a>
                        </>
                    }
                    isSelected={ termsAgreement }
                    onChange={ setTermsAgreement }
                    size="md"
                />
            ) }

            <StepFooter
                primary={ {
                    label: mode === 'login' ? tAuthMethod('signIn') : tAuthMethod('next'),
                    onClick: handleEmailAuth,
                    disabled: !canProceed,
                    loading
                } }
                back={ { show: false } }
            />
        </div>
    );
}
